/**
 * Module summary:
 * This file manages assessment records, profile data, audit entries, and photo
 * metadata. It keeps a localStorage-first working copy for fast UI updates and
 * syncs to Firebase services when the app is running in live mode.
 */

import {
  INPUT_LIMITS,
  sanitizeAssessmentResponses,
  sanitizeSingleLineText,
  toOptionalSingleLineText,
} from '../../utils/inputSecurity';
import { responseCreatesFinding, severityWeight, type SeverityLevel } from '../../utils/assessmentScoring';
import {
  ACTIVE_ASSESSMENT_TITLE,
  ACTIVE_ASSESSMENT_VERSION,
} from './assessmentCatalog';

export type AssessmentStatus = 'draft' | 'in-progress' | 'completed';
export type AssessmentAuditAction = 'created' | 'edited' | 'submitted' | 'photo_upload_completed';

export interface AssessmentAuditEvent {
  id: string;
  action: AssessmentAuditAction;
  actorUserId?: string;
  at: string;
  fromStatus?: AssessmentStatus;
  toStatus?: AssessmentStatus;
  details?: string;
}

export interface AssessmentReportSnapshot {
  templateVersion: string;
  finalizedAt: string;
  finalizedByUserId?: string;
  score?: number;
  findingsCount?: number;
}

export interface AssessmentPhoto {
  id: string;
  name: string;
  questionId?: string;
  mimeType?: string;
  sizeBytes?: number;
  createdAt?: string;
  dataUrl?: string;
  storagePath?: string;
  storageUrl?: string;
  thumbnailUrl?: string;
}

export interface AssessmentRecord {
  id: string;
  name: string;
  buildingId: string;
  assessmentId: string;
  assessmentVersion?: string;
  assessmentTitle?: string;
  status: AssessmentStatus;
  score?: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  responses: Record<string, any>;
  severitySummary?: {
    low: number;
    medium: number;
    high: number;
    critical: number;
    weightedPoints: number;
  };
  address?: string;
  addressParts?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  buildingType?: string;
  userId?: string;
  sourceAssessmentId?: string;
  photos?: Record<string, AssessmentPhoto[]>;
}

export interface UserProfileRecord {
  displayName: string;
  phone?: string;
  organization?: string;
  address?: string;
  userId?: string;
}

const ASSESS_KEY = 'macha.assessments';
const ACTIVE_KEY = 'macha.activeAssessmentId';
const PROFILE_KEY = 'macha.profile';
const CURRENT_USER_KEY = 'macha.currentUserId';
export const REPORT_TEMPLATE_VERSION = '2026.03.19.1';

// Match the same default as authService.ts — set REACT_APP_DATA_PROVIDER=local for offline development
const USE_FIREBASE = (process.env.REACT_APP_DATA_PROVIDER || 'firebase') === 'firebase';

function now() {
  // Create a fresh ISO timestamp so record updates can be sorted and compared consistently.
  return new Date().toISOString();
}

function safeEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    // Use the browser UUID helper when available so audit entries get robust unique ids.
    return crypto.randomUUID();
  }
  // Fall back to time plus randomness so older browsers can still generate event ids.
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function toIso(value: any): string {
  // Normalize Firestore timestamps and plain strings into one ISO format the UI can display safely.
  if (!value) return now();
  if (typeof value === 'string') return value;
  if (typeof value?.toDate === 'function') return value.toDate().toISOString();
  return now();
}

function getCurrentUserId(): string {
  // Read the current user id from localStorage so local-only mode can still namespace records per user.
  return localStorage.getItem(CURRENT_USER_KEY) || '';
}

export function setCurrentUserId(userId: string) {
  localStorage.setItem(CURRENT_USER_KEY, userId);
}

function userAssessKey(userId?: string): string {
  const uid = userId || getCurrentUserId();
  // Build a user-specific key so multiple users on one browser do not overwrite each other's local assessments.
  return uid ? `${ASSESS_KEY}.${uid}` : ASSESS_KEY;
}

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    // Return parsed stored data when present; otherwise fall back to the caller's default value.
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

function save<T>(key: string, value: T) {
  // Persist app state to localStorage so assessment work survives reloads.
  localStorage.setItem(key, JSON.stringify(value));
}

function hasDurablePhotoReference(photo: AssessmentPhoto): boolean {
  // Treat Storage-backed references as the durable source of truth for cross-device viewing.
  return Boolean(photo.storagePath || photo.storageUrl);
}

function stripTransientPhotoData(photos?: Record<string, AssessmentPhoto[]>): Record<string, AssessmentPhoto[]> | undefined {
  if (!photos) return undefined;

  const stripped = Object.fromEntries(
    Object.entries(photos)
      .map(([questionId, photoList]) => [
        questionId,
        photoList.map((photo) => ({
          ...photo,
          // Remove browser-only preview payloads once Firebase has a durable copy.
          dataUrl: undefined,
        })),
      ])
      .filter(([, photoList]) => photoList.length > 0)
  ) as Record<string, AssessmentPhoto[]>;

  return Object.keys(stripped).length > 0 ? stripped : undefined;
}

function buildFirestorePhotoMetadata(photo: AssessmentPhoto, fallbackQuestionId: string) {
  // Normalize optional photo fields so Firestore never receives unsupported undefined values.
  return {
    id: photo.id,
    name: photo.name,
    questionId: photo.questionId || fallbackQuestionId,
    mimeType: photo.mimeType || null,
    sizeBytes: typeof photo.sizeBytes === 'number' ? photo.sizeBytes : null,
    createdAt: photo.createdAt || now(),
    storagePath: photo.storagePath || null,
    storageUrl: photo.storageUrl || null,
    thumbnailUrl: photo.thumbnailUrl || null,
  };
}

function sanitizeAssessmentRecord(record: AssessmentRecord): AssessmentRecord {
  return {
    ...record,
    name: sanitizeSingleLineText(record.name, INPUT_LIMITS.assessmentName),
    buildingId: sanitizeSingleLineText(record.buildingId, INPUT_LIMITS.genericShortText),
    assessmentId: sanitizeSingleLineText(record.assessmentId, INPUT_LIMITS.genericShortText),
    assessmentVersion: toOptionalSingleLineText(record.assessmentVersion, INPUT_LIMITS.genericShortText),
    assessmentTitle: toOptionalSingleLineText(record.assessmentTitle, INPUT_LIMITS.assessmentName),
    responses: sanitizeAssessmentResponses(record.responses),
    severitySummary: record.severitySummary,
    address: toOptionalSingleLineText(record.address, INPUT_LIMITS.assessmentAddress),
    buildingType: toOptionalSingleLineText(record.buildingType, INPUT_LIMITS.genericShortText),
    sourceAssessmentId: toOptionalSingleLineText(record.sourceAssessmentId, INPUT_LIMITS.genericShortText),
    addressParts: record.addressParts
      ? {
          street: toOptionalSingleLineText(record.addressParts.street, INPUT_LIMITS.address),
          city: toOptionalSingleLineText(record.addressParts.city, INPUT_LIMITS.city),
          state: toOptionalSingleLineText(record.addressParts.state, 2),
          zipCode: toOptionalSingleLineText(record.addressParts.zipCode, 10),
        }
      : undefined,
  };
}

async function writeAuditEventToFirestore(
  assessmentId: string,
  action: AssessmentAuditAction,
  payload: Omit<AssessmentAuditEvent, 'id' | 'action' | 'at'>
) {
  if (!USE_FIREBASE) return;
  try {
    // Import Firestore lazily so local-only sessions do not load backend code unless syncing is needed.
    const { collection, doc, setDoc, serverTimestamp } = await import('firebase/firestore');
    const { getFirebaseDb } = await import('../firebaseConfig');
    const db = getFirebaseDb();
    const eventId = safeEventId();
    // Write one document per audit event so the history can be read back as a timeline.
    const ref = doc(collection(db, 'userAssessments', assessmentId, 'auditTrail'), eventId);
    await setDoc(ref, {
      id: eventId,
      action,
      actorUserId: payload.actorUserId || getCurrentUserId() || null,
      fromStatus: payload.fromStatus || null,
      toStatus: payload.toStatus || null,
      details: payload.details || null,
      at: serverTimestamp(),
    }, { merge: false });
  } catch (err) {
    console.error('Failed to write audit event:', err);
  }
}

export async function getAssessmentAuditTrailFromFirestore(assessmentId: string): Promise<AssessmentAuditEvent[]> {
  if (!USE_FIREBASE) return [];
  try {
    const { collection, getDocs, orderBy, query } = await import('firebase/firestore');
    const { getFirebaseDb } = await import('../firebaseConfig');
    const db = getFirebaseDb();
    const q = query(collection(db, 'userAssessments', assessmentId, 'auditTrail'), orderBy('at', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const v = d.data() as any;
      return {
        id: v.id || d.id,
        action: v.action as AssessmentAuditAction,
        actorUserId: v.actorUserId || undefined,
        at: toIso(v.at),
        fromStatus: v.fromStatus || undefined,
        toStatus: v.toStatus || undefined,
        details: v.details || undefined,
      };
    });
  } catch (err) {
    console.error('Failed to read audit trail:', err);
    return [];
  }
}

async function saveAssessmentToFirestore(record: AssessmentRecord): Promise<{ ok: boolean; error?: string }> {
  if (!USE_FIREBASE) return { ok: true };
  try {
    const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
    const { getFirebaseDb } = await import('../firebaseConfig');
    const db = getFirebaseDb();
    const sanitizedRecord = sanitizeAssessmentRecord(record);
    const resolvedUserId = sanitizedRecord.userId || getCurrentUserId() || '';
    // Stop when no owner can be resolved because the backend schema relies on user ownership.
    if (!resolvedUserId) {
      return { ok: false, error: 'No authenticated user id was available for this assessment.' };
    }
    const { photos, ...data } = sanitizedRecord;
    const firestoreData = Object.fromEntries(
      Object.entries(data).filter(([, value]) => value !== undefined)
    );
    const photoMetadata = photos
      ? Object.fromEntries(
          Object.entries(photos)
            .map(([questionId, photoList]) => [
              questionId,
              // Persist only Firebase-backed photo metadata so Firestore never becomes a second image store.
              photoList
                .filter(hasDurablePhotoReference)
                .map((photo) => buildFirestorePhotoMetadata(photo, questionId)),
            ])
            .filter(([, photoList]) => photoList.length > 0)
        )
      : undefined;
    await setDoc(doc(db, 'userAssessments', sanitizedRecord.id), {
      ...firestoreData,
      userId: resolvedUserId,
      ...(photoMetadata ? { photos: photoMetadata } : {}),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    return { ok: true };
  } catch (err) {
    console.error('Failed to save assessment to Firestore:', err);
    return { ok: false, error: err instanceof Error ? err.message : 'Unknown Firestore error.' };
  }
}

async function savePhotosMetadataSubcollection(
  assessmentId: string,
  userId: string,
  photos: Record<string, AssessmentPhoto[]>
) {
  if (!USE_FIREBASE) return;
  try {
    const { collection, doc, serverTimestamp, writeBatch } = await import('firebase/firestore');
    const { getFirebaseDb } = await import('../firebaseConfig');
    const db = getFirebaseDb();
    const batch = writeBatch(db);
    const photosCol = collection(db, 'userAssessments', assessmentId, 'photos');

    Object.entries(photos).forEach(([questionId, photoList]) => {
      photoList.filter(hasDurablePhotoReference).forEach((photo, index) => {
        // Walk each question/photo pair so every uploaded image gets its own metadata document.
        const photoId = photo.id || `${questionId}-${index}-${Date.now()}`;
        const ref = doc(photosCol, photoId);
        batch.set(ref, {
          ...buildFirestorePhotoMetadata({ ...photo, id: photoId }, questionId),
          assessmentId,
          userId,
          updatedAt: serverTimestamp(),
        }, { merge: true });
      });
    });

    await batch.commit();
  } catch (err) {
    console.error('Failed to save photo metadata subcollection:', err);
  }
}

async function loadPhotosMetadataSubcollection(assessmentId: string): Promise<Record<string, AssessmentPhoto[]>> {
  if (!USE_FIREBASE) return {};
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const { getFirebaseDb } = await import('../firebaseConfig');
    const db = getFirebaseDb();
    const snap = await getDocs(collection(db, 'userAssessments', assessmentId, 'photos'));
    const mapped: Record<string, AssessmentPhoto[]> = {};
    snap.forEach((docSnap) => {
      const p = docSnap.data() as any;
      const questionId = p.questionId || 'general';
      // Create the question bucket the first time a photo for that question is encountered.
      if (!mapped[questionId]) mapped[questionId] = [];
      mapped[questionId].push({
        id: p.id || docSnap.id,
        name: p.name || docSnap.id,
        questionId,
        mimeType: p.mimeType,
        sizeBytes: p.sizeBytes,
        createdAt: toIso(p.createdAt),
        storagePath: p.storagePath,
        storageUrl: p.storageUrl,
        thumbnailUrl: p.thumbnailUrl,
      });
    });
    return mapped;
  } catch (err) {
    console.error('Failed to load photo metadata subcollection:', err);
    return {};
  }
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  // Convert a browser data URL into a Blob because Firebase Storage uploads file-like binary objects.
  const res = await fetch(dataUrl);
  return await res.blob();
}

async function uploadPhotosToStorage(
  userId: string,
  assessmentId: string,
  photos: Record<string, {
    id: string;
    name: string;
    questionId?: string;
    mimeType?: string;
    sizeBytes?: number;
    createdAt?: string;
    dataUrl?: string;
    storagePath?: string;
    storageUrl?: string;
    thumbnailUrl?: string;
  }[]>
) {
  if (!USE_FIREBASE) return;
  try {
    const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
    const { getFirebaseStorage } = await import('../firebaseConfig');
    const storage = getFirebaseStorage();
    const uploaded: AssessmentRecord['photos'] = {};

    for (const [questionId, photoList] of Object.entries(photos)) {
      uploaded[questionId] = [];
      for (let i = 0; i < photoList.length; i += 1) {
        const photo = photoList[i];
        // Reuse previously uploaded photos so repeated saves do not create duplicate storage files.
        if (!photo.dataUrl && photo.storageUrl) {
          uploaded[questionId].push(photo);
          continue;
        }
        if (!photo.dataUrl) continue;
        // Sanitize the file name and organize uploads by user, assessment, and question for traceability.
        const ext = (photo.mimeType?.split('/')[1] || photo.name.split('.').pop() || 'jpg').toLowerCase();
        const safeName = photo.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const photoId = photo.id || `ph-${Date.now()}-${i}`;
        const storagePath = `photos/${userId}/${assessmentId}/${questionId}/${photoId}_${safeName}.${ext}`;
        const storageRef = ref(storage, storagePath);
        const blob = await dataUrlToBlob(photo.dataUrl);
        await uploadBytes(storageRef, blob, { contentType: photo.mimeType || blob.type || 'image/jpeg' });
        const storageUrl = await getDownloadURL(storageRef);
        uploaded[questionId].push({
          ...photo,
          id: photoId,
          questionId,
          storagePath,
          storageUrl,
        });
      }
    }
    return uploaded;
  } catch (err) {
    console.error('Failed to upload photos to Firebase Storage:', err);
    return photos;
  }
}

async function getStorageUrlForPath(storagePath?: string, existingUrl?: string): Promise<string | undefined> {
  if (existingUrl) return existingUrl;
  if (!storagePath || !USE_FIREBASE) return undefined;

  try {
    // Resolve a fresh download URL from the stored path so reports can recover even when older records missed the URL.
    const { ref, getDownloadURL } = await import('firebase/storage');
    const { getFirebaseStorage } = await import('../firebaseConfig');
    return await getDownloadURL(ref(getFirebaseStorage(), storagePath));
  } catch (err) {
    console.error('Failed to resolve Firebase Storage download URL:', err);
    return undefined;
  }
}

async function ensurePhotosStoredInFirebase(
  userId: string,
  assessmentId: string,
  photos?: Record<string, AssessmentPhoto[]>
): Promise<Record<string, AssessmentPhoto[]> | undefined> {
  if (!photos) return undefined;

  const uploadedPhotos = await uploadPhotosToStorage(userId, assessmentId, photos);
  if (!uploadedPhotos) return undefined;

  return stripTransientPhotoData(uploadedPhotos);
}

export async function hydrateAssessmentPhotoUrls(record: AssessmentRecord): Promise<AssessmentRecord> {
  if (!record.photos) return record;

  let changed = false;
  const hydratedEntries = await Promise.all(
    Object.entries(record.photos).map(async ([questionId, photoList]) => {
      const nextPhotos = await Promise.all(
        photoList.map(async (photo) => {
          const resolvedUrl = await getStorageUrlForPath(photo.storagePath, photo.storageUrl);
          if (resolvedUrl && resolvedUrl !== photo.storageUrl) {
            changed = true;
            return { ...photo, storageUrl: resolvedUrl };
          }
          return photo;
        })
      );
      return [questionId, nextPhotos] as const;
    })
  );

  if (!changed) return record;

  const hydratedRecord: AssessmentRecord = {
    ...record,
    photos: Object.fromEntries(hydratedEntries),
  };

  upsertAssessment(hydratedRecord);
  return hydratedRecord;
}

export function listAssessments(): AssessmentRecord[] {
  // Sort newest-first so dashboards and lists show the most recently updated record first.
  return load<AssessmentRecord[]>(userAssessKey(), []).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function listAssessmentsByUser(userId: string): AssessmentRecord[] {
  return load<AssessmentRecord[]>(userAssessKey(userId), []).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function deleteAssessment(id: string): Promise<void> {
  const found = getAssessmentById(id);
  const key = userAssessKey(found?.userId);
  const all = load<AssessmentRecord[]>(key, []).filter((a) => a.id !== id);
  save(key, all);

  if (getActiveAssessmentId() === id) {
    localStorage.removeItem(ACTIVE_KEY);
  }

  if (!USE_FIREBASE) return;

  try {
    const { deleteDoc, doc } = await import('firebase/firestore');
    const { getFirebaseDb } = await import('../firebaseConfig');
    const db = getFirebaseDb();
    await deleteDoc(doc(db, 'userAssessments', id));
  } catch (err) {
    console.error('Failed to delete assessment from Firestore:', err);
    throw err instanceof Error ? err : new Error('Failed to delete assessment.');
  }
}

export function upsertAssessment(record: AssessmentRecord) {
  const key = userAssessKey(record.userId);
  const all = load<AssessmentRecord[]>(key, []);
  const idx = all.findIndex(x => x.id === record.id);
  // Replace the existing record when the id already exists; otherwise insert at
  // the front so a new assessment appears immediately in recent lists.
  if (idx >= 0) all[idx] = record;
  else all.unshift(record);
  save(key, all);
}

export async function createAssessment(input: {
  name: string;
  buildingId: string;
  assessmentId: string;
  assessmentVersion?: string;
  assessmentTitle?: string;
  address?: string;
  addressParts?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  buildingType?: string;
  userId?: string;
  sourceAssessmentId?: string;
}): Promise<AssessmentRecord> {
  const resolvedUserId = input.userId || getCurrentUserId() || undefined;
  const id = `AS-${Date.now()}`;
  // Build the initial record with an in-progress status so the user can start
  // editing immediately after creation.
  const rec: AssessmentRecord = {
    id,
    name: sanitizeSingleLineText(input.name, INPUT_LIMITS.assessmentName),
    buildingId: sanitizeSingleLineText(input.buildingId, INPUT_LIMITS.genericShortText),
    assessmentId: sanitizeSingleLineText(input.assessmentId, INPUT_LIMITS.genericShortText),
    assessmentVersion: toOptionalSingleLineText(input.assessmentVersion || ACTIVE_ASSESSMENT_VERSION, INPUT_LIMITS.genericShortText),
    assessmentTitle: toOptionalSingleLineText(input.assessmentTitle || ACTIVE_ASSESSMENT_TITLE, INPUT_LIMITS.assessmentName),
    status: 'in-progress',
    createdAt: now(),
    updatedAt: now(),
    responses: {},
    address: toOptionalSingleLineText(input.address, INPUT_LIMITS.assessmentAddress),
    addressParts: input.addressParts
      ? {
          street: toOptionalSingleLineText(input.addressParts.street, INPUT_LIMITS.address),
          city: toOptionalSingleLineText(input.addressParts.city, INPUT_LIMITS.city),
          state: toOptionalSingleLineText(input.addressParts.state, 2),
          zipCode: toOptionalSingleLineText(input.addressParts.zipCode, 10),
        }
      : undefined,
    buildingType: toOptionalSingleLineText(input.buildingType, INPUT_LIMITS.genericShortText),
    userId: resolvedUserId,
    sourceAssessmentId: toOptionalSingleLineText(input.sourceAssessmentId, INPUT_LIMITS.genericShortText),
  };
  upsertAssessment(rec);
  const firestoreWrite = await saveAssessmentToFirestore(rec);
  await writeAuditEventToFirestore(id, 'created', {
    actorUserId: resolvedUserId || getCurrentUserId() || undefined,
    fromStatus: 'draft',
    toStatus: 'in-progress',
    details: input.sourceAssessmentId
      ? `Follow-up assessment created from ${input.sourceAssessmentId}.`
      : 'Assessment created.',
  });
  setActiveAssessmentId(id);
  if (USE_FIREBASE && !firestoreWrite.ok) {
    throw new Error(
      `Assessment was created locally, but Firebase sync failed. ${firestoreWrite.error || 'Please reopen Reports and confirm the record syncs before continuing.'}`
    );
  }
  return rec;
}

export async function createFollowUpAssessment(
  source: AssessmentRecord,
  overrides?: {
    name?: string;
    address?: string;
    addressParts?: AssessmentRecord['addressParts'];
    buildingType?: string;
  }
): Promise<AssessmentRecord> {
  return createAssessment({
    name: sanitizeSingleLineText(overrides?.name || source.name, INPUT_LIMITS.assessmentName),
    buildingId: source.buildingId,
    assessmentId: source.assessmentId,
    assessmentVersion: source.assessmentVersion,
    assessmentTitle: source.assessmentTitle,
    address: toOptionalSingleLineText(overrides?.address ?? source.address, INPUT_LIMITS.assessmentAddress),
    addressParts: overrides?.addressParts ?? source.addressParts,
    buildingType: toOptionalSingleLineText(overrides?.buildingType ?? source.buildingType, INPUT_LIMITS.genericShortText),
    userId: source.userId,
    sourceAssessmentId: source.id,
  });
}

export function getAssessmentById(id: string): AssessmentRecord | null {
  return listAssessments().find(x => x.id === id) ?? null;
}

function normalizeFirestoreRecord(id: string, data: any): AssessmentRecord {
  const mappedPhotos = data.photos
    ? Object.fromEntries(
        Object.entries(data.photos as Record<string, any[]>).map(([questionId, photoList]) => [
          questionId,
          (photoList || []).map((photo: any) => ({
            id: photo.id,
            name: photo.name,
            questionId: photo.questionId || questionId,
            mimeType: photo.mimeType,
            sizeBytes: photo.sizeBytes,
            createdAt: toIso(photo.createdAt),
            storagePath: photo.storagePath,
            storageUrl: photo.storageUrl,
            thumbnailUrl: photo.thumbnailUrl,
          })),
        ])
      )
    : undefined;

  return {
    id,
    name: data.name || id,
    buildingId: data.buildingId || 'building-001',
    assessmentId: data.assessmentId || 'facility-security-v2',
    assessmentVersion: data.assessmentVersion || undefined,
    assessmentTitle: data.assessmentTitle || undefined,
    status: (data.status as AssessmentStatus) || 'in-progress',
    score: typeof data.score === 'number' ? data.score : undefined,
    createdAt: toIso(data.createdAt),
    updatedAt: toIso(data.updatedAt),
    completedAt: data.completedAt ? toIso(data.completedAt) : undefined,
    responses: (data.responses || {}) as Record<string, any>,
    severitySummary: data.severitySummary || undefined,
    address: data.address,
    addressParts: data.addressParts,
    buildingType: data.buildingType,
    userId: data.userId,
    sourceAssessmentId: data.sourceAssessmentId,
    photos: mappedPhotos,
  };
}

export async function syncAssessmentsByUser(userId: string): Promise<AssessmentRecord[]> {
  if (!userId) return listAssessmentsByUser(userId);
  if (!USE_FIREBASE) return listAssessmentsByUser(userId);
  try {
    // Query only this user's records so sync stays scoped to the data they are allowed to access.
    const { collection, getDocs, query, where } = await import('firebase/firestore');
    const { getFirebaseDb } = await import('../firebaseConfig');
    const db = getFirebaseDb();
    const q = query(collection(db, 'userAssessments'), where('userId', '==', userId));
    const snap = await getDocs(q);
    const remoteRows = snap.docs.map((d) => normalizeFirestoreRecord(d.id, d.data()));
    save(userAssessKey(userId), remoteRows);
    return remoteRows.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  } catch (err) {
    console.error('Failed to sync assessments from Firestore:', err);
    return listAssessmentsByUser(userId);
  }
}

export async function getAssessmentByIdWithRemoteFallback(id: string): Promise<AssessmentRecord | null> {
  const local = getAssessmentById(id);
  if (local) return local;
  if (!USE_FIREBASE) return null;
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { getFirebaseDb } = await import('../firebaseConfig');
    const db = getFirebaseDb();
    const snap = await getDoc(doc(db, 'userAssessments', id));
    if (!snap.exists()) return null;
    const remote = normalizeFirestoreRecord(snap.id, snap.data());
    if (remote.userId) {
      const photos = await loadPhotosMetadataSubcollection(id);
      if (Object.keys(photos).length > 0) {
        remote.photos = photos;
      }
    }
    upsertAssessment(remote);
    return remote;
  } catch (err) {
    console.error('Failed to load assessment from Firestore:', err);
    return null;
  }
}

export function setActiveAssessmentId(id: string) {
  localStorage.setItem(ACTIVE_KEY, id);
}

export function getActiveAssessmentId(): string | null {
  return localStorage.getItem(ACTIVE_KEY);
}

export async function saveAssessmentProgress(
  id: string,
  responses: Record<string, any>,
  photos?: Record<string, AssessmentPhoto[]>
): Promise<boolean> {
  const found = getAssessmentById(id);
  if (!found) return false;
  const nextStatus: AssessmentStatus = 'in-progress';
  const persistedPhotos = USE_FIREBASE && found.userId && photos
    ? await ensurePhotosStoredInFirebase(found.userId, id, photos)
    : photos || found.photos;
  // Preserve the existing record and overwrite only the fields that should change on a save.
  const updated: AssessmentRecord = {
    ...found,
    responses: sanitizeAssessmentResponses(responses),
    photos: persistedPhotos,
    status: nextStatus,
    updatedAt: now(),
  };
  upsertAssessment(updated);
  await saveAssessmentToFirestore(updated);
  if (persistedPhotos && found.userId) {
    await savePhotosMetadataSubcollection(id, found.userId, persistedPhotos);
  }
  void writeAuditEventToFirestore(id, 'edited', {
    actorUserId: found.userId || getCurrentUserId() || undefined,
    fromStatus: found.status,
    toStatus: nextStatus,
    details: 'Assessment progress saved.',
  });
  return true;
}

export async function completeAssessment(
  id: string,
  responses: Record<string, any>,
  photos?: Record<string, {
    id: string;
    name: string;
    questionId?: string;
    mimeType?: string;
    sizeBytes?: number;
    createdAt?: string;
    dataUrl?: string;
    storagePath?: string;
    storageUrl?: string;
    thumbnailUrl?: string;
  }[]>
) {
  const found = getAssessmentById(id);
  if (!found) return;
  const questionDefinition = await import('./AssessmentLoader').then((mod) => mod.loadAssessment(found.assessmentId)).catch(() => null);
  const questionMeta = new Map<string, { question: any; severityLevel: SeverityLevel; severityWeight: number }>();
  questionDefinition?.categories.forEach((category) => {
    category.questions.forEach((question) => {
      const level = question.severityLevel || 'medium';
      questionMeta.set(question.id, {
        question,
        severityLevel: level,
        severityWeight: severityWeight(level, question.severityWeight),
      });
    });
  });

  let findingsCount = 0;
  let weightedFindings = 0;
  let weightedPossible = 0;
  const severitySummary = {
    low: 0,
    medium: 0,
    high: 0,
    critical: 0,
    weightedPoints: 0,
  };

  Object.entries(responses).forEach(([key, value]) => {
    if (key.endsWith('Comment')) return;

    const meta = questionMeta.get(key) || { question: undefined, severityLevel: 'medium' as const, severityWeight: 4 };
    if (typeof value === 'boolean') {
      weightedPossible += meta.severityWeight;
      if (responseCreatesFinding(meta.question, value)) {
        findingsCount += 1;
        weightedFindings += meta.severityWeight;
        severitySummary[meta.severityLevel] += 1;
        severitySummary.weightedPoints += meta.severityWeight;
      }
      return;
    }

    if (responseCreatesFinding(meta.question, value)) {
      findingsCount += 1;
      weightedFindings += meta.severityWeight;
      severitySummary[meta.severityLevel] += 1;
      severitySummary.weightedPoints += meta.severityWeight;
    }
  });

  const score = weightedPossible > 0
    ? Math.max(0, Math.min(100, Math.round(((weightedPossible - weightedFindings) / weightedPossible) * 100)))
    : 100;
  const finalizedAt = now();
  const nextStatus: AssessmentStatus = 'completed';
  const persistedPhotos = USE_FIREBASE && found.userId && photos
    ? await ensurePhotosStoredInFirebase(found.userId, id, photos)
    : photos || found.photos;
  const updated: AssessmentRecord = {
    ...found,
    responses: sanitizeAssessmentResponses(responses),
    status: nextStatus,
    score,
    updatedAt: finalizedAt,
    completedAt: finalizedAt,
    severitySummary,
    photos: persistedPhotos,
  };
  upsertAssessment(updated);
  await saveAssessmentToFirestore(updated);
  if (persistedPhotos && found.userId) {
    await savePhotosMetadataSubcollection(id, found.userId, persistedPhotos);
  }
  void writeAuditEventToFirestore(id, 'submitted', {
    actorUserId: found.userId || getCurrentUserId() || undefined,
    fromStatus: found.status,
    toStatus: nextStatus,
    details: `Assessment submitted with score ${score}; findings ${findingsCount}; template ${REPORT_TEMPLATE_VERSION}.`,
  });
  if (persistedPhotos && found.userId) {
    void writeAuditEventToFirestore(id, 'photo_upload_completed', {
      actorUserId: found.userId || getCurrentUserId() || undefined,
      fromStatus: updated.status,
      toStatus: updated.status,
      details: 'Photo upload and metadata sync completed.',
    });
  }
}

export function getProfile(): UserProfileRecord {
  return load<UserProfileRecord>(PROFILE_KEY, { displayName: '' });
}

export function saveProfile(profile: UserProfileRecord) {
  save(PROFILE_KEY, {
    ...profile,
    displayName: sanitizeSingleLineText(profile.displayName, INPUT_LIMITS.displayName),
    phone: toOptionalSingleLineText(profile.phone, INPUT_LIMITS.genericShortText),
    organization: toOptionalSingleLineText(profile.organization, INPUT_LIMITS.organization),
    address: toOptionalSingleLineText(profile.address, INPUT_LIMITS.address),
  });
}
